<script setup lang="ts" generic="T extends Record<string, any>">
    import { useDialogConfirm } from 'composables'
    import { useI18n } from 'vue-i18n'
    import PkFieldset from './PkFieldset.vue'
    import { computed, watch } from 'vue'

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const props = withDefaults(
        defineProps<{
            modelValue: Partial<T>[] | undefined
            label?: string
            placeholder?: string
            defaultValue?: Partial<T> | (() => Partial<T>)
            move?: boolean
            showReset?: boolean
            invalid?: boolean
            invalidLabel?: string | string[]
            confirm?: boolean
            readonly?: boolean
            allowEmpty?: boolean
            force?: boolean
        }>(),
        {
            label: undefined,
            placeholder: undefined,
            defaultValue: () => ({}),
            invalidLabel: undefined,
            confirm: true,
            readonly: false,
            allowEmpty: false,
        },
    )
    const emit = defineEmits<{
        'update:modelValue': [value: Partial<T>[]]
    }>()
    // Explicit two-way computed instead of useVModel: with an optional array
    // prop its overloads infer an unusable setter type (`Partial<T>[] & true`)
    // and the component fails to typecheck. Normalizing undefined to [] on get
    // also spares every consumer of the value a null guard.
    const localModelValue = computed<Partial<T>[]>({
        get: () => props.modelValue ?? [],
        set: (value) => emit('update:modelValue', value),
    })
    const addItem = () => {
        const newItem =
            typeof props.defaultValue === 'function'
                ? props.defaultValue()
                : (props.defaultValue ?? {})
        const appended = [...localModelValue.value, newItem]
        localModelValue.value = props.force
            ? JSON.parse(JSON.stringify(appended))
            : appended
    }
    watch(
        localModelValue,
        (newValue) => {
            if (newValue.length === 0 && !props.allowEmpty) {
                addItem()
            }
        },
        { immediate: true },
    )
    const { openDialogConfirm } = useDialogConfirm()
    const removeItem = async (index: number) => {
        if (props.confirm) {
            const result = await openDialogConfirm()
            if (!result) {
                return
            }
        }
        localModelValue.value = localModelValue.value.filter(
            (_, i) => i !== index,
        )
    }
    const reset = async () => {
        if (props.confirm) {
            const result = await openDialogConfirm()
            if (!result) {
                return
            }
        }
        emit('update:modelValue', [])
    }
    const moveUp = (index: number) => {
        if (index === 0) {
            return
        }
        const newValue = [...localModelValue.value]
        const [item] = newValue.splice(index, 1)
        newValue.splice(index - 1, 0, item)
        localModelValue.value = newValue
    }
    const moveDown = (index: number) => {
        if (index === localModelValue.value.length - 1) {
            return
        }
        const newValue = [...localModelValue.value]
        const [item] = newValue.splice(index, 1)
        newValue.splice(index + 1, 0, item)
        localModelValue.value = newValue
    }
    defineExpose({
        addItem,
        removeItem,
        moveUp,
        moveDown,
    })
</script>

<template>
    <div
        class="pk-editor-repeater"
        :class="{
            'pk-editor-repeater--invalid': invalid,
            'pk-editor-repeater--empty': !localModelValue?.length,
        }">
        <PkFieldset :legend="label">
            <div class="pk-editor-repeater__wrapper">
                <ul class="pk-editor-repeater__list">
                    <li
                        v-for="(item, index) in localModelValue"
                        :key="index"
                        class="pk-editor-repeater__item">
                        <div class="pk-editor-repeater__item-wrapper">
                            <slot v-bind="{ index, item, readonly }" />
                        </div>
                        <div
                            v-if="!readonly"
                            class="pk-editor-repeater__item-actions">
                            <VvButtonGroup
                                modifiers="compact vertical"
                                item-modifiers="action-quiet">
                                <VvButton
                                    v-if="move"
                                    :title="$t('action.moveUp')"
                                    icon="ri:arrow-up-line"
                                    :disabled="index === 0"
                                    @click.stop="moveUp(index)" />
                                <VvButton
                                    :title="$t('action.remove')"
                                    icon="ri:delete-bin-line"
                                    @click.stop="removeItem(index)" />
                                <VvButton
                                    v-if="move"
                                    :title="$t('action.moveDown')"
                                    icon="ri:arrow-down-line"
                                    :disabled="
                                        index === localModelValue.length - 1
                                    "
                                    @click.stop="moveDown(index)" />
                            </VvButtonGroup>
                        </div>
                    </li>
                </ul>
                <div class="pk-editor-repeater__actions">
                    <div
                        v-if="invalid"
                        class="pk-editor-repeater__invalid-label">
                        {{
                            Array.isArray(invalidLabel)
                                ? invalidLabel.join(', ')
                                : invalidLabel
                        }}
                    </div>
                    <div
                        v-else-if="placeholder && localModelValue.length === 0"
                        class="pk-editor-repeater__placeholder">
                        {{ placeholder }}
                    </div>
                    <VvButtonGroup v-if="!readonly" class="mt-4">
                        <slot name="actions" />
                        <VvButton
                            v-if="showReset"
                            :label="$t('action.reset')"
                            icon="ri:delete-bin-line"
                            modifiers="action-quiet"
                            :disabled="localModelValue.length === 0"
                            @click.stop="reset" />
                        <VvButton
                            :label="$t('action.add')"
                            icon="ri:add-line"
                            :modifiers="
                                showReset ? 'action' : 'action-quiet-small'
                            "
                            :icon-position="showReset ? undefined : 'right'"
                            @click.stop="addItem" />
                    </VvButtonGroup>
                </div>
            </div>
        </PkFieldset>
    </div>
</template>

<style lang="scss">
    @layer volver.components {
        .pk-editor-repeater {
            padding-top: var(--spacing-8);
            margin-bottom: var(--spacing-md);

            .pk-fieldset {
                position: relative;
                padding: 0;
                margin: 0;

                legend {
                    position: absolute;
                    top: calc(-1 * var(--spacing-8));
                    left: var(--spacing-md);
                }
            }

            &__wrapper {
                overflow: hidden;
            }

            &__list {
                display: flex;
                flex-direction: column;
            }

            &__item {
                display: flex;
                border-bottom: var(--border) solid var(--color-surface-3);

                &-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    flex-grow: 1;
                    padding-top: var(--spacing-md);
                    padding-inline: var(--spacing-md);
                }

                &:first-child {
                    .pk-editor-repeater__item-wrapper {
                        padding-top: var(--spacing-24);
                    }
                }

                &-actions {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    flex-shrink: 0;
                    padding: var(--spacing-6);
                    background-color: var(--color-surface-1);
                    border-left: var(--border) solid var(--color-surface-3);
                }
            }

            &__actions {
                min-height: var(--spacing-40);
                display: flex;
                justify-content: flex-end;
                padding: var(--input-spacing-top) var(--input-spacing-right)
                    var(--input-spacing-bottom) var(--input-spacing-left);
            }

            &__placeholder {
                color: var(--input-label-color);
                font-size: var(--input-hint-font-size);
                display: flex;
                align-items: center;
                margin-right: auto;
                padding: var(--spacing-12) var(--spacing-8) var(--spacing-8) 0;
            }

            &__invalid-label {
                color: var(--input-invalid-color);
                font-size: var(--input-hint-font-size);
                display: flex;
                align-items: center;
                margin-right: auto;
            }

            &--invalid {
                .pk-editor-repeater__wrapper {
                    border-color: var(--color-danger);
                }

                .pk-editor-repeater__legend {
                    color: var(--color-danger);
                }
            }
        }
    }
</style>
